import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  IncidentWorkItemId,
  IncidentWorkItemWithContext,
  S136FormRejectedMinimumFields,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  getWorkflowTranslationContext,
  IncidentFormTypeSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../api/index.js";
import { useToastNotifications } from "../../../hooks/useToastNotifications.js";
import { FormRejectedModal } from "../../../modals/FormRejected/FormRejected.js";
import { Error as ErrorComponent } from "../../../pages/Error/Error.js";
import { createRoute } from "../../../routing/createRoute.js";
import { ActionBanner } from "../../ActionBanner/ActionBanner.js";

interface S136FormRejectedNotificationProps {
  incidentWorkItemId: IncidentWorkItemId;
  notificationId: string;
  close: () => void;
}

function getLatestS136FormRejectedData(
  incidentWorkItem: IncidentWorkItemWithContext,
): S136FormRejectedMinimumFields | undefined {
  const s136RejectedForms = incidentWorkItem.forms.filter(
    (form) => form.formType === IncidentFormTypeSchema.enum.s136FormRejected,
  );
  if (s136RejectedForms.length === 0) {
    return;
  }
  // Get the s136 form with the most recent updatedAt date
  s136RejectedForms.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
  const latestS136RejectedForm = s136RejectedForms[0];
  if (latestS136RejectedForm === undefined) {
    return;
  }
  const sections = latestS136RejectedForm.sections;
  const s136FormRejectedSection = sections.find(
    (section) => section.id === IncidentFormTypeSchema.enum.s136FormRejected,
  );

  if (!s136FormRejectedSection || !s136FormRejectedSection.data) {
    return;
  }

  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;
  const schema = workflows[
    workflowVersion
  ]?.s136FormRejected.formDefinition.sections.find(
    (section) => section.id === FormSectionSchema.enum.s136FormRejected,
  );

  if (!schema) {
    return;
  }

  const result = schema
    .buildFormSpecification({})
    .formSchema.safeParse(s136FormRejectedSection.data);
  if (!result.success) {
    return;
  } else {
    return s136FormRejectedSection.data as S136FormRejectedMinimumFields;
  }
}

export const S136FormRejectedNotification = ({
  incidentWorkItemId,
  notificationId,
  close,
}: S136FormRejectedNotificationProps) => {
  const { t } = useTranslation();
  const notifications = useToastNotifications();
  const navigate = useNavigate();
  const [isFormRejectedModalOpen, setIsFormRejectedModalOpen] = useState(true);
  const [{ data: incidentWorkItem, error }] =
    apiHooks.incidentWorkItem.get(incidentWorkItemId);
  const [formRejectedData, setFormRejectedData] = useState<{
    reason: string;
    userName: string;
    userEmail: string;
  } | null>(null);

  useEffect(() => {
    if (incidentWorkItem) {
      const s136FormRejectedData =
        getLatestS136FormRejectedData(incidentWorkItem);
      if (s136FormRejectedData) {
        setFormRejectedData({
          reason: s136FormRejectedData.reason.value,
          userName: s136FormRejectedData.user.value,
          userEmail: s136FormRejectedData.userEmail.value,
        });
      } else {
        setFormRejectedData(null);
        notifications.show(t("notification.failedRetrieve"), {
          severity: "error",
        });
      }
    }
  }, [incidentWorkItem]);

  if (error) return <ErrorComponent />;

  const translationContext = getWorkflowTranslationContext({
    incidentWorkItem,
  });

  const handleCloseFormRejectedModal = () => {
    setIsFormRejectedModalOpen(false);
  };

  const handleClose = async () => {
    setIsFormRejectedModalOpen(false);
    close();
    const res = await api.notifications.markAsRead(notificationId);
    if (res.status !== 204) {
      notifications.show(t("notifications.failedMarkAsRead"), {
        severity: "error",
      });
    }
  };

  const handleShare = async () => {
    setIsFormRejectedModalOpen(false);
    navigate(createRoute.incidentSharePage(incidentWorkItemId));
    close();
    const res = await api.notifications.markAsRead(notificationId);
    if (res.status !== 204) {
      notifications.show(t("notifications.failedMarkAsRead"), {
        severity: "error",
      });
    }
  };

  return formRejectedData ? (
    <>
      <Box
        sx={{
          padding: ".5rem",
        }}
      >
        <ActionBanner
          title={t("s136FormRejectedNotification.s136Rejected", {
            context: translationContext,
          })}
          bannerType={"warning"}
          onClick={() => {
            setIsFormRejectedModalOpen(true);
          }}
        />
      </Box>
      {isFormRejectedModalOpen && (
        <FormRejectedModal
          open={isFormRejectedModalOpen}
          close={handleCloseFormRejectedModal}
          onClose={() => void handleClose()}
          onShare={() => void handleShare()}
          reason={formRejectedData.reason}
          userName={formRejectedData.userName}
          userEmail={formRejectedData.userEmail}
          translationContext={translationContext}
        />
      )}
    </>
  ) : (
    <CircularProgress />
  );
};
